import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme, THEMES, type SkinOverride, type ThemeFamilyId } from '@evi/skins';
import { useAuthStore } from '@/store/auth';
import { fetchActiveSkin } from '@/lib/api';

/**
 * Syncs the driver's purchased `active_skin_code` into the
 * ThemeProvider as a runtime override. Mounted once at the top of
 * the authenticated tree; re-runs when the user swaps skins via the
 * Unlocks store (React-Query invalidation) or when the driver flips
 * the Light/Dark mode toggle.
 *
 * Resolver picks the skin's light or dark variant block from
 * `metadata.variants[mode]` based on the driver's current mode —
 * the skin no longer forces a mode. Falls back to the legacy flat
 * `accentColor`/`chargingColor` fields if a doc hasn't been migrated
 * yet, so pre-migration databases keep rendering.
 *
 * No-op when unauth'd (guest + login pages use the tenant default
 * theme) or when the driver has no active skin.
 */
export function useSkinSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { setSkinOverride, mode } = useTheme();

  const q = useQuery({
    queryKey: ['unlocks', 'active-skin'],
    queryFn: fetchActiveSkin,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setSkinOverride(null);
      return;
    }
    const skin = q.data?.skin;
    if (!skin) {
      setSkinOverride(null);
      return;
    }
    const meta = (skin.metadata ?? {}) as Record<string, unknown>;
    const override: SkinOverride = {};

    // Driver Themes ticket — registry-driven lookup replaces the
    // legacy hardcoded ['default', 'luce'] allow-list. No existing
    // skin metadata declares a base family outside default/luce so
    // no current user is affected; the fix completes the registry-
    // driven unification across the package so future skins whose
    // metadata declares a different base family (e.g. obsidian)
    // apply their composition correctly. typeof narrows the unknown
    // meta value to string before the `in THEMES` runtime check.
    const themeId = meta['themeId'];
    if (typeof themeId === 'string' && themeId in THEMES) {
      override.themeId = themeId as ThemeFamilyId;
    }

    // New shape: pick the variant block matching the driver's mode.
    const variants = meta['variants'] as
      | { light?: Record<string, unknown>; dark?: Record<string, unknown> }
      | undefined;
    const v = variants?.[mode];

    // Legacy flat shape (pre-migration): fall through to top-level
    // accentColor / chargingColor so un-migrated documents still render.
    const accent = (v?.['accentColor'] as string | undefined) ?? (meta['accentColor'] as string | undefined);
    const charging = (v?.['chargingColor'] as string | undefined) ?? (meta['chargingColor'] as string | undefined);
    const surface = v?.['surface'] as string | undefined;
    const bg = v?.['bg'] as string | undefined;
    // Extended overrides (added for Bloom) — present only on skins whose
    // pastel surface tones need border + muted-text re-tuning against
    // the base family's calibration.
    const border = v?.['border'] as string | undefined;
    const textMute = v?.['textMute'] as string | undefined;

    if (typeof accent === 'string') override.accentColor = accent;
    if (typeof charging === 'string') override.chargingColor = charging;
    if (typeof surface === 'string') override.surface = surface;
    if (typeof bg === 'string') override.bg = bg;
    if (typeof border === 'string') override.border = border;
    if (typeof textMute === 'string') override.textMute = textMute;

    setSkinOverride(override);
  }, [isAuthenticated, q.data, mode, setSkinOverride]);
}
